import React, { useEffect, useMemo, useState } from 'react';
import { RichTextEditor, RichTextEditorControl } from '@mantine/tiptap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { usePuckRichTextStyle } from '../../../context/PuckRichTextStyleContext';
import { useGoogleFonts } from '../../../utils/proposalStylesUtils';
import {
  FONT_SIZE_REM_DEFAULT,
  formatFontSizeRem,
  getSelectionDefaultFontFamily,
  getSelectionOwnFontFamily,
  parseFontSizeRem,
  stepFontSizeRem,
} from '../../../utils/proposalRichTextFonts';
import { getSelectionFontSizeRem } from '../../../utils/richTextSelectionFontSize';
import RichTextBubbleFontFamilyPicker from './RichTextBubbleFontFamilyPicker';
import ThemeFontFamilyPicker from './ThemeFontFamilyPicker';
import pickerStyles from './RichTextBubbleFontFamilyPicker.module.css';

// Affichage en px (1rem = 16px) : la donnée reste stockée en rem.
// FONT_SIZE_REM_STEP (0.0625rem) = 1px, la conversion est exacte.
const remToPx = (rem) => Math.round(rem * 16);

export default function RichTextBubbleTypographyControls({
  editor,
  rememberNow,
  runWithSelection,
  lockBubbleMenu,
  unlockBubbleMenu,
  onRestoreSelection,
}) {
  const { fontFamilies, fontSizes } = usePuckRichTextStyle();
  // Chaque police du thème s'écrit dans sa police dans la liste.
  const themeFontValues = useMemo(
    () => fontFamilies.map((f) => f.value),
    [fontFamilies],
  );
  useGoogleFonts(themeFontValues);
  const [fontFamily, setFontFamily] = useState(null);
  const [defaultFontFamily, setDefaultFontFamily] = useState(null);
  const [fontSizeRem, setFontSizeRem] = useState(null);
  // Taille posée sur le texte, par opposition à celle qu'il hérite du style :
  // seule la première peut être retirée.
  const [hasOwnFontSize, setHasOwnFontSize] = useState(false);

  useEffect(() => {
    if (!editor) return undefined;

    const syncFromSelection = () => {
      setFontFamily(getSelectionOwnFontFamily(editor));
      setDefaultFontFamily(getSelectionDefaultFontFamily(editor));
      setFontSizeRem(getSelectionFontSizeRem(editor));
      setHasOwnFontSize(Boolean(editor.getAttributes('textStyle')?.fontSize));
    };

    syncFromSelection();
    editor.on('selectionUpdate', syncFromSelection);
    editor.on('transaction', syncFromSelection);
    return () => {
      editor.off('selectionUpdate', syncFromSelection);
      editor.off('transaction', syncFromSelection);
    };
  }, [editor]);

  const fontSizeOptions = useMemo(
    () => fontSizes.map(({ value }) => ({
      value,
      label: `${remToPx(parseFontSizeRem(value))} px`,
    })),
    [fontSizes],
  );

  const applyFontSizeRem = (rem) => {
    rememberNow();
    runWithSelection((chain) => chain.setFontSize(formatFontSizeRem(rem)));
    setFontSizeRem(rem);
  };

  const handleStepMouseDown = (direction) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    applyFontSizeRem(stepFontSizeRem(fontSizeRem, direction));
  };

  const applyListedFontSize = (value) => {
    if (value == null) {
      rememberNow();
      runWithSelection((chain) => chain.unsetFontSize());
      return;
    }
    applyFontSizeRem(parseFontSizeRem(value));
  };

  const applyFontFamily = (value) => {
    setFontFamily(value);
    if (!value) {
      runWithSelection((chain) => chain.unsetFontFamily(), { focus: false });
      return;
    }
    runWithSelection((chain) => chain.setFontFamily(value), { focus: false });
  };

  if (!fontFamilies.length && !fontSizes.length) return null;

  return (
    <RichTextEditor.ControlsGroup>
      {fontFamilies.length > 0 && (
        <ThemeFontFamilyPicker
          value={fontFamily}
          options={fontFamilies}
          defaultFont={defaultFontFamily}
          onChange={applyFontFamily}
          rememberNow={rememberNow}
          lockBubbleMenu={lockBubbleMenu}
          unlockBubbleMenu={unlockBubbleMenu}
          onRestoreSelection={onRestoreSelection}
        />
      )}
      {fontSizes.length > 0 && (
        <>
          <RichTextEditorControl
            aria-label="Réduire la taille"
            title="Réduire la taille"
            onMouseDown={handleStepMouseDown(-1)}
          >
            <FontAwesomeIcon icon={icon({ name: 'minus', style: 'solid' })} style={{ fontSize: 14 }} />
          </RichTextEditorControl>
          <RichTextBubbleFontFamilyPicker
            value={fontSizeRem != null ? formatFontSizeRem(fontSizeRem) : null}
            options={fontSizeOptions}
            onChange={applyListedFontSize}
            rememberNow={rememberNow}
            lockBubbleMenu={lockBubbleMenu}
            unlockBubbleMenu={unlockBubbleMenu}
            onRestoreSelection={onRestoreSelection}
            searchable={false}
            previewFont={false}
            clearable={hasOwnFontSize}
            clearLabel="Taille par défaut"
            triggerClassName={pickerStyles.sizeTrigger}
            triggerText={`${remToPx(fontSizeRem ?? FONT_SIZE_REM_DEFAULT)} px`}
          />
          <RichTextEditorControl
            aria-label="Augmenter la taille"
            title="Augmenter la taille"
            onMouseDown={handleStepMouseDown(1)}
          >
            <FontAwesomeIcon icon={icon({ name: 'plus', style: 'solid' })} style={{ fontSize: 14 }} />
          </RichTextEditorControl>
        </>
      )}
    </RichTextEditor.ControlsGroup>
  );
}
